import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NbDialogService } from '@nebular/theme';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SelectionType, TableColumn } from '@swimlane/ngx-datatable';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { createTag, deleteTag, updateTag } from '../../app.actions';
import { AppState, Contract, Tag } from '../../app.models';
import { selectContract, selectCustomerApiEndpoint } from '../../app.selector';
import { DateTimePipe } from '../pipes/DateTimePipe';


@Component({
  selector: 'app-tags-overview',
  templateUrl: './tags-overview.component.html',
  styleUrls: ['./tags-overview.component.scss']
})
export class TagsOverviewComponent implements OnInit, AfterViewInit, OnDestroy {
  contract: Contract;
  selectedContract$: Subscription;
  env = environment;
  columns: TableColumn[];
  @ViewChild('buttonsTemplate') buttonsTemplate: TemplateRef<any>;
  @ViewChild('treeToggleTemplate') treeToggleTemplate: TemplateRef<any>;
  @ViewChild('treeLabelTemplate') treeLabelTemplate: TemplateRef<any>;
  @ViewChild('deleteDialog') deleteDialog: TemplateRef<any>;
  @ViewChild('editDialog') editDialog: TemplateRef<any>;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('nameHdrTpl') nameHdrTpl: TemplateRef<any>;


  data: Tag[];
  SelectionType = SelectionType;
  $count: Observable<number>;
  menu$: Subscription;
  endpoint: string;
  treeStatus = {};
  plainData: any;
  expiration: Date = null;

  constructor(
    public translate: TranslateService,
    private store: Store,
    private title: Title,
    private cd: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private http: HttpClient
  ) {
  }

  ngOnInit() {
    // contract subscription
    this.selectedContract$ = this.store.select(selectContract)
      .pipe(
        filter(c => !!c),
        distinctUntilChanged((curr, prev) => curr.clientId === prev.clientId)
      )
      .subscribe((c) => {
        this.contract = c;
        this.title.setTitle(`Tags - ${c.name}`);
      });
  }

  ngAfterViewInit() {

    this.searchInput.nativeElement.focus();
    this.columns = [
      {
        name: 'Actions',
        prop: '',
        headerTemplate: this.nameHdrTpl,
        cellTemplate: this.buttonsTemplate,
        maxWidth: 140,
        minWidth: 140,
        sortable: false
      },
      {
        name: 'Name',
        prop: 'name',
        headerTemplate: this.nameHdrTpl,
        isTreeColumn: true,
        treeToggleTemplate: this.treeToggleTemplate,
        cellTemplate: this.treeLabelTemplate,
        treeLevelIndent: 15,
      },
      { name: 'Source Id', prop: 'sourceId', headerTemplate: this.nameHdrTpl },
      { name: 'Expiry Date', prop: 'expiration', headerTemplate: this.nameHdrTpl, pipe: new DateTimePipe() },
    ];
    this.store.select((state: AppState) => state.tags)
      .subscribe(d => {
        const deepCopy = JSON.parse(JSON.stringify(d));
        this.plainData = deepCopy.map(e => ({ ...e, treeStatus: this.treeStatus[e.id] }));
        this.data = [...this.plainData]
      });

    // uncomment to test Test with a lot of data (~4500 tags)
    // this.data = mock.map(e=>({
    //   ...e,
    //   treeStatus:'collapsed',
    // }));

    this.$count = this.store.select((state: AppState) => state.tags.length);

    this.store.select(selectCustomerApiEndpoint).subscribe(e => {
      this.endpoint = e;
    })

    this.cd.detectChanges();
  }
  handleClickAddChildTag(row: Tag) {
    const tag = { id: null, parentId: row.id, expiration: 0 } as unknown as Tag;
    this.dialogService.open(this.editDialog, { context: tag })
  }

  handleClickDelete(tag: Tag) {
    this.cd.detectChanges();
    const context = {
      tag,
      delete: () => this.store.dispatch(deleteTag({ payload: tag }))
    }
    // https://github.com/swimlane/ngx-datatable/issues/721#issuecomment-325550591
    setTimeout(() => {
      this.dialogService.open(this.deleteDialog, { context });
    })
  }

  handleClickAdd(tag: Tag) {
    this.cd.detectChanges();
  }

  handleClickQuickEdit(row) {
    this.http.get(`${this.endpoint}/tags/${row.id}`)
      .pipe(
        switchMap((tag: Tag) => this.dialogService.open(this.editDialog, { context: tag }).onClose)
      ).subscribe();
  }

  handleClickSave(tag: Tag) {
    tag.expiration = this.expiration ? this.expiration.getTime() : 0
    this.expiration = null;
    if (tag.id) {
      this.store.dispatch(updateTag({ payload: tag }));
    } else {
      this.store.dispatch(createTag({ payload: tag }));
    }
  }

  addParentsOfFiltered(tags: Tag[] = [], parentId = -1): Tag[] {
    if (parentId === -1) {
      tags.forEach((t: Tag) => {
        this.addParentsOfFiltered(tags, t.parentId)
      });
      return tags;
    }

    const parent = this.plainData.find(t => t.id === parentId);
    const exists = tags.find(t => t.id === parentId);
    if (parentId && parent && !exists) {
      tags.push({ ...parent, treeStatus: 'expanded' });
      this.addParentsOfFiltered(tags, parent.parentId);
    }
  }

  updateFilter(event) {
    const input = event.target.value.toLowerCase();
    if (input === '') {
      this.data = [...this.plainData];
      return;
    }
    const filtered = this.plainData.filter((tag: Tag) => tag.name.toLowerCase().includes(input));
    this.data = this.addParentsOfFiltered(filtered);
  }

  onTreeAction(event: any) {
    const row = event.row ? event.row : event;
    if (row.treeStatus !== 'expanded') {
      row.treeStatus = 'expanded';
    } else {
      row.treeStatus = 'collapsed';
    }
    this.treeStatus[row.id] = row.treeStatus;
    this.data = [...this.data];
  }

  hasChilds(row) {
    return !!this.data.find(r => r.parentId === row.id);
  }

  ngOnDestroy() {
    this.selectedContract$.unsubscribe();
  }
}
